import { _ } from "i18n";
import "./pageControls.scss";
import type { PaginationState, RowData, Table } from "@tanstack/react-table";
import type React from "react";
import type { ReactNode } from "react";

/** Page size option - can be a number or "all" for showing all items */
export type PageSizeOption = number | "all";

/** Default page size options */
export const DEFAULT_PAGE_SIZE_OPTIONS: PageSizeOption[] = [5, 10, 20, 30, 50, 100, "all"];

interface TablePageControlsProps<T extends RowData> {
	table: Table<T>;
	/** Show page size selector */
	showPageSizeSelector?: boolean;
	/** Available page size options (defaults to [5, 10, 20, 30, 50, 100, "all"]) */
	pageSizeOptions?: PageSizeOption[];
	/** Total number of items (required when "all" option is used) */
	totalItems?: number;
	/** Pagination state setter (required for page size selector) */
	setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>;
	/** Data fetch failed; show retry in the bar when onRetry is provided */
	isError?: boolean;
	/** Called when user clicks Retry after a failed load */
	onRetry?: () => void;
	/** Rendered on the right side of the pagination row when set */
	paginationFooterInfo?: ReactNode;
}

export default function TablePageControls<T extends RowData>({
	table,
	showPageSizeSelector = false,
	pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
	totalItems,
	setPagination,
	isError = false,
	onRetry,
	paginationFooterInfo,
}: TablePageControlsProps<T>) {
	const handleInputPageChange = (newPage: number) => {
		if (newPage > 0 && newPage <= table.getPageCount()) {
			setTimeout(() => {
				table.setPageIndex(newPage - 1);
			}, 500);
		}
	};

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		const newSize =
			value === "all"
				? totalItems ?? 10000 // Use totalItems or fallback to large number
				: Number(value);

		if (setPagination) {
			setPagination((prev) => ({
				...prev,
				pageSize: newSize,
				pageIndex: 0, // Reset to first page when changing page size
			}));
		} else {
			table.setPageSize(newSize);
			table.setPageIndex(0);
		}
	};

	const currentPageSize = table.getState().pagination.pageSize;

	return (
		<div className="tablePageControls-wrapper">
			<div className="tablePageControls-container">
				<>
					<div
						className="tablePageControls-goToStartEnd"
						onClick={() => {
							if (table.getCanPreviousPage()) table.firstPage();
						}}
					>
						<span className={`icon-new icon-chevron-left-grey ${table.getCanPreviousPage() ? "icon-blue" : ""} icon-24`} />
						<span className={`icon-new icon-chevron-left-grey ${table.getCanPreviousPage() ? "icon-blue" : ""} icon-24`} />
					</div>
					<div
						className="tablePageControls-prevNext"
						onClick={() => {
							if (table.getCanPreviousPage()) table.previousPage();
						}}
					>
						<span className={`icon-new icon-chevron-left-grey ${table.getCanPreviousPage() ? "icon-blue" : ""} icon-24`} />
					</div>

					<div
						className="tablePageControls-prevNext"
						onClick={() => {
							if (table.getCanNextPage()) table.nextPage();
						}}
					>
						<span className={`icon-new icon-chevron-right-grey ${table.getCanNextPage() ? "icon-blue" : ""} icon-24`} />
					</div>
					<div
						className="tablePageControls-goToStartEnd"
						onClick={() => {
							if (table.getCanNextPage()) table.lastPage();
						}}
					>
						<span className={`icon-new icon-chevron-right-grey ${table.getCanNextPage() ? "icon-blue" : ""} icon-24`} />
						<span className={`icon-new icon-chevron-right-grey ${table.getCanNextPage() ? "icon-blue" : ""} icon-24`} />
					</div>
					<div className="tablePageControls-count">{`${_("label.pagination.page")} ${table.getState().pagination.pageIndex + 1} ${_(
						"label.pagination.of"
					)} ${table.getPageCount()} | ${_("label.pagination.go.to.page")}`}</div>
					<div>
						<input
							key={table.getState().pagination.pageIndex}
							className="tablePageControls-input"
							defaultValue={table.getState().pagination.pageIndex + 1}
							type="number"
							min={1}
							max={table.getPageCount()}
							onChange={(e) => handleInputPageChange(Number(e.target.value))}
						/>
					</div>
				</>
				{showPageSizeSelector && (
					<div className="tablePageControls-pageSizeSelector">
						<label htmlFor="pageSize">{_("label.pagination.rows_per_page") || "Rows per page"}:</label>
						<select id="pageSize" value={currentPageSize} onChange={handlePageSizeChange}>
							{pageSizeOptions.map((option) => (
								<option key={String(option)} value={option === "all" ? totalItems ?? 10000 : option}>
									{option === "all" ? _("label.pagination.all") || "All" : option}
								</option>
							))}
						</select>
					</div>
				)}
				{isError && onRetry && (
					<div className="tablePageControls-retry">
						<span className="tablePageControls-retryMessage">{"Failed to load."}</span>
						<button type="button" className="tablePageControls-retryBtn" onClick={onRetry}>
							{_("label.retry") || "Retry"}
						</button>
					</div>
				)}
			</div>
			{paginationFooterInfo ? <div className="tablePageControls-footerInfo">{paginationFooterInfo}</div> : null}
		</div>
	);
}
